import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Filter, SlidersHorizontal, ArrowUpDown, Heart, ImageIcon, MapPin, BadgeCheck, ChevronDown, Check, ArrowLeft, Search } from 'lucide-react';
import FiltersModal from './FiltersModal';
import SortModal from './SortModal';
import { trackPageView } from '../analytics';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Map navigation slugs to backend category UUIDs
const CATEGORY_SLUG_MAP = {
  'motors': 'a0e6743f-43d3-48b7-9d16-59715302d258',
  'property-rent': 'f7cd3654-4e80-4db2-a914-e15f94379df8',
  'property-sale': 'f7cd3654-4e80-4db2-a914-e15f94379df8',
  'off-plan': 'f7cd3654-4e80-4db2-a914-e15f94379df8',
  'rooms': 'f7cd3654-4e80-4db2-a914-e15f94379df8',
  'jobs': '6e25c2fa-7606-4b95-a06c-1c511b0666d2',
  'classifieds': '64ca667c-b06a-470c-82a7-f0af29b95b3a',
  'furniture': '46c7fa4e-113e-4fba-81cf-93c3e9f219ba',
  'community': '62e17658-14d6-4339-8032-6bc787b6688d',
  'electronics': '64ca667c-b06a-470c-82a7-f0af29b95b3a',
  'fashion': '05655b67-780c-48a7-932f-1ab5a024d1f0',
  'pets': 'b28f01b9-400d-4b09-99e8-35c899c8a475',
  'baby-kids': '7213bbf1-dac6-455f-bc28-464589fc11e7',
  'hobbies-sports': '4bc1ae80-069a-46cb-82ab-33532c541424',
  'services': '62e17658-14d6-4339-8032-6bc787b6688d',
};

// Reverse map: UUID → filter type for FiltersModal
const CATEGORY_UUID_TO_TYPE = {
  'a0e6743f-43d3-48b7-9d16-59715302d258': 'motors',
  'f7cd3654-4e80-4db2-a914-e15f94379df8': 'property',
  '6e25c2fa-7606-4b95-a06c-1c511b0666d2': 'jobs',
  '64ca667c-b06a-470c-82a7-f0af29b95b3a': 'classifieds',
  '46c7fa4e-113e-4fba-81cf-93c3e9f219ba': 'furniture',
  '62e17658-14d6-4339-8032-6bc787b6688d': 'community',
  '05655b67-780c-48a7-932f-1ab5a024d1f0': 'classifieds',
  'b28f01b9-400d-4b09-99e8-35c899c8a475': 'classifieds',
  '7213bbf1-dac6-455f-bc28-464589fc11e7': 'classifieds',
  '4bc1ae80-069a-46cb-82ab-33532c541424': 'classifieds',
};

const resolveCategoryId = (raw) => {
  if (!raw) return '';
  // If it's already a UUID, return as-is
  if (raw.includes('-') && raw.length > 20) return raw;
  // Otherwise map from slug
  return CATEGORY_SLUG_MAP[raw] || '';
};

const SearchResults = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const isAr = i18n.language === 'ar';

  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [showSortModal, setShowSortModal] = useState(false);
  const [showVerifiedFirst, setShowVerifiedFirst] = useState(false);
  const [categories, setCategories] = useState([]);
  const [locations, setLocations] = useState([]);

  // Read URL params and resolve category slug → UUID
  const rawCategory = searchParams.get('category_id') || searchParams.get('category') || '';
  const resolvedCategoryId = resolveCategoryId(rawCategory);

  const [filters, setFilters] = useState({
    search: searchParams.get('search') || searchParams.get('q') || '',
    category_id: resolvedCategoryId,
    subcategory: searchParams.get('subcategory') || '',
    location_id: searchParams.get('location_id') || searchParams.get('location') || '',
    min_price: searchParams.get('min_price') || '',
    max_price: searchParams.get('max_price') || '',
    condition: searchParams.get('condition') || '',
    sort: searchParams.get('sort') || 'newest',
    // Motors
    make: searchParams.get('make') || searchParams.get('brand') || '',
    body_type: searchParams.get('body_type') || '',
    min_year: searchParams.get('min_year') || '',
    max_year: searchParams.get('max_year') || '',
    min_mileage: searchParams.get('min_mileage') || '',
    max_mileage: searchParams.get('max_mileage') || '',
    fuel_type: searchParams.get('fuel_type') || '',
    transmission: searchParams.get('transmission') || '',
    color: searchParams.get('color') || '',
    seller_type: searchParams.get('seller_type') || '',
    // Property
    bedrooms: searchParams.get('bedrooms') || '',
    bathrooms: searchParams.get('bathrooms') || '',
    furnishing: searchParams.get('furnishing') || '',
    min_size: searchParams.get('min_size') || '',
    max_size: searchParams.get('max_size') || '',
    // Jobs
    job_type: searchParams.get('job_type') || '',
    experience: searchParams.get('experience') || '',
  });

  useEffect(() => { trackPageView('search'); fetchMetadata(); }, []);
  useEffect(() => { fetchListings(); }, [searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchMetadata = async () => {
    try {
      const [catRes, locRes] = await Promise.all([axios.get(`${API}/categories`), axios.get(`${API}/locations`)]);
      setCategories(catRes.data); setLocations(locRes.data);
    } catch (e) { console.error(e); }
  };

  const fetchListings = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      // Map frontend keys to backend query params
      const backendKeyMap = {
        category_id: 'category_id',
        subcategory: 'subcategory_id',
        location_id: 'location_id',
        min_price: 'min_price',
        max_price: 'max_price',
        condition: 'condition',
        search: 'search',
        make: 'make',
        body_type: 'body_type',
        min_year: 'min_year',
        max_year: 'max_year',
        min_mileage: 'min_mileage',
        max_mileage: 'max_mileage',
        fuel_type: 'fuel_type',
        transmission: 'transmission',
        color: 'color',
        seller_type: 'seller_type',
        bedrooms: 'bedrooms',
        bathrooms: 'bathrooms',
        furnishing: 'furnishing',
        min_size: 'min_size',
        max_size: 'max_size',
        job_type: 'job_type',
        experience: 'experience',
      };

      Object.entries(backendKeyMap).forEach(([frontKey, backKey]) => {
        const val = filters[frontKey];
        if (val) params.append(backKey, val);
      });

      const response = await axios.get(`${API}/listings?${params.toString()}`);
      let results = response.data;
      if (filters.sort === 'price_low') results.sort((a, b) => a.price - b.price);
      else if (filters.sort === 'price_high') results.sort((a, b) => b.price - a.price);
      else if (filters.sort === 'oldest') results.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
      setListings(results);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };

  const handleFilterChange = (key, value) => {
    const newF = { ...filters, [key]: value }; setFilters(newF);
    const params = new URLSearchParams();
    Object.entries(newF).forEach(([k, v]) => { if (v) params.set(k, v); });
    setSearchParams(params);
  };

  const clearFilters = () => {
    const empty = Object.fromEntries(Object.keys(filters).map(k => [k, k === 'sort' ? 'newest' : '']));
    setFilters(empty); setSearchParams({});
  };

  const formatPrice = (price) => `${t('common.aed')} ${new Intl.NumberFormat(isAr ? 'ar-AE' : 'en-AE').format(price)}`;

  // Determine the filter type for the modal based on resolved category + subcategory
  const getFilterType = () => {
    const sub = filters.subcategory || '';
    if (filters.category_id) {
      const baseType = CATEGORY_UUID_TO_TYPE[filters.category_id] || 'general';
      // For motors, differentiate by subcategory
      if (baseType === 'motors') {
        if (['motorcycles'].includes(sub)) return 'motorcycles';
        if (['boats'].includes(sub)) return 'boats';
        if (['heavy-vehicles'].includes(sub)) return 'heavy-vehicles';
        if (['auto-accessories'].includes(sub)) return 'auto-accessories';
        if (['number-plates'].includes(sub)) return 'number-plates';
        return 'cars'; // used-cars, new-cars, export-cars, rental-cars, or generic motors
      }
      return baseType;
    }
    return 'general';
  };

  const selectClass = "w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl outline-none focus:border-[#E31C25]/50 text-white text-sm transition-all appearance-none";
  const selectStyle = { backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='rgba(255,255,255,0.4)' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 12px center' };

  // Map FiltersModal keys → SearchResults state keys
  const modalKeyMap = {
    minPrice: 'min_price', maxPrice: 'max_price', location: 'location_id',
    condition: 'condition', category_id: 'category_id',
    make: 'make', bodyType: 'body_type', minYear: 'min_year', maxYear: 'max_year',
    minKm: 'min_mileage', maxKm: 'max_mileage', fuelType: 'fuel_type',
    transmission: 'transmission', color: 'color', sellerType: 'seller_type',
    keywords: 'search',
    bedrooms: 'bedrooms', bathrooms: 'bathrooms', furnishing: 'furnishing',
    minSize: 'min_size', maxSize: 'max_size',
    jobType: 'job_type', experience: 'experience',
    subcategory: 'subcategory',
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#1A1A2E] via-[#16213E] to-[#0F3460] pb-40 md:pb-8">
      {/* Dark Hero Header */}
      <div className="hero-gradient relative">
        <div className="noise-overlay" />
        <div className="max-w-6xl mx-auto px-4 pt-4 pb-6 relative z-10">
          <div className="flex items-center justify-between mb-5">
            <button onClick={() => navigate(-1)} className="w-9 h-9 rounded-xl bg-white/8 flex items-center justify-center hover:bg-white/15 transition-all active:scale-90" data-testid="back-btn">
              <ArrowLeft className="w-5 h-5 text-white" />
            </button>
            <button onClick={() => setShowFilters(!showFilters)}
              className="lg:hidden flex items-center gap-2 px-3.5 py-2 bg-white/8 rounded-xl hover:bg-white/15 transition-all" data-testid="filters-toggle">
              <Filter className="w-4 h-4 text-white" />
              <span className="text-sm text-white font-medium">{t('filter.filters')}</span>
            </button>
          </div>
          <div className="animate-fade-up">
            <h1 className="text-2xl md:text-3xl font-extrabold text-white font-heading tracking-tight">
              {filters.search ? `"${filters.search}"` : t('search.allListings')}
            </h1>
            <p className="text-white/40 text-sm mt-1">{listings.length} {t('search.adsFound')}</p>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 pt-4">
        <div className="flex flex-col lg:flex-row gap-4">
          {/* Sidebar Filters (Desktop) */}
          <div className={`lg:w-72 ${showFilters ? 'block' : 'hidden lg:block'}`}>
            <div className="bg-white/[0.03] backdrop-blur-sm rounded-2xl border border-white/[0.06] p-4 sticky top-4">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-bold text-white text-sm flex items-center gap-2">
                  <SlidersHorizontal className="w-4 h-4 text-white/40" />
                  {t('filter.filters')}
                </h2>
                <button onClick={clearFilters} className="text-xs text-[#E31C25] font-bold hover:text-[#ff3a42]" data-testid="clear-filters">
                  {t('filter.clear')}
                </button>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-semibold text-white/50 uppercase tracking-wider mb-2">{t('filter.category')}</label>
                  <select value={filters.category_id} onChange={e => handleFilterChange('category_id', e.target.value)}
                    className={selectClass} style={selectStyle} data-testid="filter-category">
                    <option value="" className="bg-[#1A1A2E] text-white/50">{t('filter.all')}</option>
                    {categories.map(cat => <option key={cat.id} value={cat.id} className="bg-[#1A1A2E] text-white">{cat.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-white/50 uppercase tracking-wider mb-2">{t('filter.location')}</label>
                  <select value={filters.location_id} onChange={e => handleFilterChange('location_id', e.target.value)}
                    className={selectClass} style={selectStyle} data-testid="filter-location">
                    <option value="" className="bg-[#1A1A2E] text-white/50">{t('filter.all')}</option>
                    {locations.map(loc => <option key={loc.id} value={loc.id} className="bg-[#1A1A2E] text-white">{loc.city}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-white/50 uppercase tracking-wider mb-2">{t('filter.price')}</label>
                  <div className="grid grid-cols-2 gap-2">
                    <input type="number" placeholder={t('filter.min')} value={filters.min_price}
                      onChange={e => handleFilterChange('min_price', e.target.value)}
                      className="w-full px-3 py-2.5 bg-white/5 border border-white/10 rounded-xl outline-none focus:border-[#E31C25]/50 text-white placeholder-white/25 text-sm" data-testid="filter-min-price" />
                    <input type="number" placeholder={t('filter.max')} value={filters.max_price}
                      onChange={e => handleFilterChange('max_price', e.target.value)}
                      className="w-full px-3 py-2.5 bg-white/5 border border-white/10 rounded-xl outline-none focus:border-[#E31C25]/50 text-white placeholder-white/25 text-sm" data-testid="filter-max-price" />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-white/50 uppercase tracking-wider mb-2">{t('listing.condition')}</label>
                  <div className="flex gap-1.5">
                    {[{ id: '', label: t('filter.all') }, { id: 'new', label: t('listing.new') }, { id: 'used', label: t('listing.used') }].map(c => (
                      <button key={c.id} onClick={() => handleFilterChange('condition', c.id)}
                        className={`flex-1 px-3 py-2 rounded-xl text-xs font-bold transition-all ${filters.condition === c.id ? 'bg-[#E31C25] text-white shadow-lg shadow-[#E31C25]/20' : 'bg-white/[0.06] text-white/40 hover:bg-white/[0.1]'}`}
                        data-testid={`condition-${c.id || 'all'}`}>{c.label}</button>
                    ))}
                  </div>
                </div>
                <button onClick={() => setShowFilters(false)}
                  className="lg:hidden w-full py-3 bg-[#E31C25] text-white rounded-xl font-bold text-sm shadow-lg shadow-[#E31C25]/20" data-testid="apply-filters">
                  {t('filter.apply')}
                </button>
              </div>
            </div>
          </div>

          {/* Results */}
          <div className="flex-1">
            {/* Filter Chips */}
            <div className="flex flex-wrap items-center gap-2 mb-4">
              <button onClick={() => setShowFilters(true)}
                className="flex items-center gap-1.5 px-3.5 py-2 bg-white/[0.06] border border-white/[0.08] rounded-full text-sm text-white/60 hover:bg-white/[0.1] transition-all font-medium" data-testid="filter-chip">
                <SlidersHorizontal className="w-3.5 h-3.5" />{isAr ? 'الفلاتر' : 'Filters'}
              </button>
              <button onClick={() => setShowSortModal(true)}
                className="flex items-center gap-1.5 px-3.5 py-2 bg-white/[0.06] border border-white/[0.08] rounded-full text-sm text-white/60 hover:bg-white/[0.1] transition-all font-medium" data-testid="sort-chip">
                <ArrowUpDown className="w-3.5 h-3.5" />{isAr ? 'ترتيب' : 'Sort'}<ChevronDown className="w-3 h-3" />
              </button>
              <button onClick={() => setShowVerifiedFirst(!showVerifiedFirst)}
                className={`flex items-center gap-1.5 px-3.5 py-2 rounded-full text-sm font-medium transition-all ${
                  showVerifiedFirst ? 'bg-[#E31C25] text-white shadow-lg shadow-[#E31C25]/20' : 'bg-white/[0.06] border border-white/[0.08] text-white/60 hover:bg-white/[0.1]'
                }`} data-testid="verified-toggle">
                <BadgeCheck className="w-3.5 h-3.5" />{isAr ? 'الموثقين أولاً' : 'Verified First'}
                {showVerifiedFirst && <Check className="w-3 h-3" />}
              </button>
              {/* Active filter badges */}
              {filters.make && (
                <span className="flex items-center gap-1 px-3 py-1.5 bg-[#E31C25]/20 border border-[#E31C25]/30 rounded-full text-xs text-[#E31C25] font-semibold">
                  {filters.make}
                  <button onClick={() => handleFilterChange('make', '')} className="ml-1 hover:text-white">x</button>
                </span>
              )}
              {filters.fuel_type && (
                <span className="flex items-center gap-1 px-3 py-1.5 bg-[#E31C25]/20 border border-[#E31C25]/30 rounded-full text-xs text-[#E31C25] font-semibold">
                  {filters.fuel_type}
                  <button onClick={() => handleFilterChange('fuel_type', '')} className="ml-1 hover:text-white">x</button>
                </span>
              )}
              {filters.job_type && (
                <span className="flex items-center gap-1 px-3 py-1.5 bg-[#E31C25]/20 border border-[#E31C25]/30 rounded-full text-xs text-[#E31C25] font-semibold">
                  {filters.job_type}
                  <button onClick={() => handleFilterChange('job_type', '')} className="ml-1 hover:text-white">x</button>
                </span>
              )}
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-20">
                <div className="w-9 h-9 border-[3px] border-[#E31C25] border-t-transparent rounded-full animate-spin" />
              </div>
            ) : listings.length === 0 ? (
              <div className="bg-white/[0.03] backdrop-blur-sm rounded-2xl border border-white/[0.06] p-12 text-center">
                <div className="w-16 h-16 bg-white/[0.05] rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Search className="w-7 h-7 text-white/20" />
                </div>
                <p className="text-white font-bold text-sm mb-1">{t('search.noResults')}</p>
                <p className="text-white/30 text-xs mb-5">{isAr ? 'جرب تعديل الفلاتر' : 'Try adjusting your filters'}</p>
                <button onClick={clearFilters}
                  className="px-6 py-2.5 bg-[#E31C25] text-white rounded-xl font-bold text-sm shadow-lg shadow-[#E31C25]/20 active:scale-[0.98]" data-testid="clear-all-btn">
                  {t('filter.clearFilters')}
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2.5">
                {listings.map((listing, i) => (
                  <div key={listing.id} onClick={() => navigate(`/listing/${listing.id}`)}
                    className={`animate-scale-in stagger-${Math.min(i + 1, 9)} bg-white/[0.03] backdrop-blur-sm rounded-2xl overflow-hidden border border-white/[0.06] cursor-pointer group hover:border-[#E31C25]/20 transition-all`}
                    data-testid={`listing-${listing.id}`}>
                    <div className="relative aspect-[4/3] bg-white/[0.06] overflow-hidden">
                      {listing.images?.[0] ? (
                        <img src={listing.images[0].thumbnail_url || listing.images[0].image_url} alt={listing.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <ImageIcon className="w-10 h-10 text-white/10" />
                        </div>
                      )}
                      <div className="absolute top-2 left-2 flex gap-1.5">
                        {listing.is_featured && (
                          <span className="px-2 py-0.5 bg-gradient-to-r from-amber-500 to-orange-500 text-white text-[9px] font-bold rounded-full shadow-sm">TURBO</span>
                        )}
                        {listing.is_verified && (
                          <span className="px-1.5 py-0.5 bg-blue-500/80 backdrop-blur-sm text-white text-[9px] font-bold rounded-full flex items-center gap-0.5">
                            <BadgeCheck className="w-3 h-3" />
                          </span>
                        )}
                      </div>
                      <button className="absolute top-2 right-2 w-8 h-8 bg-black/30 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-black/50 transition-all"
                        onClick={e => e.stopPropagation()} data-testid={`fav-btn-${listing.id}`}>
                        <Heart className="w-4 h-4 text-white/70" strokeWidth={2} />
                      </button>
                      {listing.images?.length > 1 && (
                        <div className="absolute bottom-2 right-2 px-1.5 py-0.5 bg-black/50 backdrop-blur-sm rounded-md text-white text-[9px] font-medium flex items-center gap-0.5">
                          <ImageIcon className="w-3 h-3" />{listing.images.length}
                        </div>
                      )}
                    </div>
                    <div className="p-3">
                      <p className="text-sm font-extrabold text-[#E31C25] mb-0.5">{formatPrice(listing.price)}</p>
                      <h3 className="font-medium text-white/80 line-clamp-1 text-xs mb-1.5">{listing.title}</h3>
                      <span className="flex items-center gap-1 text-[10px] text-white/30">
                        <MapPin className="w-3 h-3" />{listing.location?.city || 'UAE'}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <FiltersModal isOpen={showFilters} onClose={() => setShowFilters(false)} filters={filters}
        onApply={(newFilters) => {
          const merged = { ...filters };
          Object.entries(newFilters).forEach(([key, value]) => {
            const mappedKey = modalKeyMap[key] || key;
            if (value && value !== 'all') {
              merged[mappedKey] = value;
            } else {
              merged[mappedKey] = '';
            }
          });
          setFilters(merged);
          const params = new URLSearchParams();
          Object.entries(merged).forEach(([k, v]) => { if (v) params.set(k, v); });
          setSearchParams(params); setShowFilters(false);
        }}
        resultCount={listings.length}
        filterType={getFilterType()}
        category={categories.find(c => c.id === filters.category_id)?.name || ''} />

      <SortModal isOpen={showSortModal} onClose={() => setShowSortModal(false)}
        currentSort={filters.sort} onSelect={sort => handleFilterChange('sort', sort)} />
    </div>
  );
};

export default SearchResults;
